/**
 * Anmeldung. Gibt es noch keinen Benutzer (leere Produktions-DB, B-S1), zeigt die Seite stattdessen die Einrichtung
 * des ersten Admins: Token aus dem Server-Log, Name, E-Mail, eigenes Passwort (Mindestlänge).
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { PASSWORT_MIN_LAENGE } from '@storeos/contracts';
import { einrichten, login, sitzung } from '../api';

export function Login({ onLogin }: { onLogin: () => void }) {
  const s = useQuery({ queryKey: ['sitzung-login'], queryFn: sitzung, retry: false });
  if (s.data?.einrichtung) return <Einrichtung onFertig={onLogin} />;
  return <Anmeldung onLogin={onLogin} />;
}

function Anmeldung({ onLogin }: { onLogin: () => void }) {
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [fehler, setFehler] = useState<string | null>(null);
  const navigate = useNavigate();

  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    try {
      await login(email, passwort);
      onLogin();
      navigate('/');
    } catch (err) {
      setFehler((err as Error).message);
    }
  }

  return (
    <div className="login">
      <form onSubmit={absenden}>
        <h1>StoreOS</h1>
        <label>E-Mail<input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></label>
        <label>Passwort<input type="password" autoComplete="current-password" value={passwort} onChange={(e) => setPasswort(e.target.value)} /></label>
        {fehler && <p className="fehler" role="alert">{fehler}</p>}
        <button type="submit">Anmelden</button>
        {import.meta.env.DEV && <p className="hinweis">Lokale Seed-Benutzer: admin / leitung / lager @storeos.local, Passwort = Rolle.{import.meta.env.VITE_API_MOCK === '1' ? ' SPA-Mock aktiv: keine API, Daten nur im Browser.' : ''}</p>}
      </form>
    </div>
  );
}

function Einrichtung({ onFertig }: { onFertig: () => void }) {
  const [w, setW] = useState({ token: '', name: '', email: '', passwort: '', wdh: '' });
  const [fehler, setFehler] = useState<string | null>(null);
  const navigate = useNavigate();
  const kurz = w.passwort.length < PASSWORT_MIN_LAENGE;
  async function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler(null);
    if (w.passwort !== w.wdh) { setFehler('Passwörter stimmen nicht überein'); return; }
    try {
      await einrichten({ token: w.token.trim(), name: w.name, email: w.email, passwort: w.passwort });
      onFertig();
      navigate('/einstellungen');
    } catch (err) { setFehler((err as Error).message); }
  }
  const feld = (k: keyof typeof w, label: string, typ = 'text', auto?: string) => (
    <label>{label}<input type={typ} autoComplete={auto} value={w[k]} onChange={(e) => setW({ ...w, [k]: e.target.value })} data-testid={`einrichtung-${k}`} /></label>
  );
  return (
    <div className="login">
      <form onSubmit={absenden} data-testid="einrichtung">
        <h1>StoreOS einrichten</h1>
        <p className="hinweis">Noch kein Benutzer angelegt. Den Einrichtungs-Token schreibt der Server beim Start ins Log („Einrichtungs-Token: …“). Alternativ auf dem Server: <code>npm run admin:anlegen -- --email …</code></p>
        {feld('token', 'Einrichtungs-Token', 'text', 'off')}
        {feld('name', 'Dein Name', 'text', 'name')}
        {feld('email', 'E-Mail (Login)', 'email', 'username')}
        {feld('passwort', `Passwort (mindestens ${PASSWORT_MIN_LAENGE} Zeichen)`, 'password', 'new-password')}
        {feld('wdh', 'Passwort wiederholen', 'password', 'new-password')}
        {fehler && <p className="fehler" role="alert">{fehler}</p>}
        <button type="submit" disabled={kurz || !w.token.trim()}>Admin anlegen und anmelden</button>
      </form>
    </div>
  );
}
